import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { User, Session } from '@supabase/supabase-js'
import { supabase } from '@/shared/lib/supabase'

interface AuthContextType {
  user: User | null
  session: Session | null
  loading: boolean
  profileReady: boolean
  signIn: (email: string, password: string) => Promise<{ data: unknown; error: { message: string } | null }>
  signUp: (email: string, password: string) => Promise<{ data: unknown; error: { message: string } | null }>
  signOut: () => Promise<void>
  resetPassword: (email: string) => Promise<{ error: { message: string } | null }>
  updatePassword: (newPassword: string) => Promise<{ error: { message: string } | null }>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

/**
 * Check/create sgw_user_profiles row for current user.
 * Tabla compartida con LegalTools: columnas son (id, user_id, preferences,
 * usage_count, last_used_at, auth_provider, ...). NO tiene email/display_name
 * porque eso vive en auth.users.
 */
async function ensureSgwProfile(user: User): Promise<boolean> {
  if (!supabase) return false
  try {
    const { data: existing } = await supabase
      .from('sgw_user_profiles')
      .select('id')
      .eq('user_id', user.id)
      .maybeSingle()

    if (existing) return true

    const { error: insertError } = await supabase
      .from('sgw_user_profiles')
      .insert({ user_id: user.id })

    if (insertError) {
      console.error('Error creating sgw_user_profiles:', insertError)
      return false
    }
    return true
  } catch (err) {
    console.error('Unexpected error in ensureSgwProfile:', err)
    return false
  }
}

/**
 * Register app install in shared user_app_installs table.
 * Schema: (id, user_id, application_id, app_name, installed_at). No tiene
 * last_login. Hacemos check-then-insert para evitar duplicados sin depender
 * de un unique constraint especifico.
 */
async function registerAppInstall(user: User): Promise<void> {
  if (!supabase) return
  try {
    const { data: existing } = await supabase
      .from('user_app_installs')
      .select('id')
      .eq('user_id', user.id)
      .eq('app_name', 'signwiz')
      .maybeSingle()

    if (existing) return

    await supabase
      .from('user_app_installs')
      .insert({ user_id: user.id, app_name: 'signwiz' })
  } catch (err) {
    // Non-critical - don't block auth flow
    console.error('Error registering app install:', err)
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [profileReady, setProfileReady] = useState(false)

  // Ensure profile exists when user changes
  useEffect(() => {
    if (user) {
      const initializeProfile = async () => {
        await ensureSgwProfile(user)
        await registerAppInstall(user)
        setProfileReady(true)
      }
      initializeProfile()
    } else {
      setProfileReady(false)
    }
  }, [user])

  useEffect(() => {
    if (!supabase) {
      // Supabase not configured - skip auth, allow anonymous usage
      setLoading(false)
      return
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      setUser(session?.user ?? null)
      setLoading(false)
    })

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session)
      setUser(session?.user ?? null)
      setLoading(false)
      if (event === 'SIGNED_OUT') {
        setProfileReady(false)
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  const signIn = async (email: string, password: string) => {
    if (!supabase) return { data: null, error: { message: 'Supabase no configurado' } }
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    return { data, error }
  }

  const signUp = async (email: string, password: string) => {
    if (!supabase) return { data: null, error: { message: 'Supabase no configurado' } }
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/login`,
      },
    })
    if (error) return { data, error }
    // Supabase retorna user populado pero identities=[] cuando el email ya esta registrado y confirmado
    if (data?.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
      return { data, error: { message: 'User already registered' } }
    }
    return { data, error: null }
  }

  const signOut = async () => {
    if (!supabase) return
    await supabase.auth.signOut()
  }

  const resetPassword = async (email: string) => {
    if (!supabase) return { error: { message: 'Supabase no configurado' } }
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    })
    return { error }
  }

  const updatePassword = async (newPassword: string) => {
    if (!supabase) return { error: { message: 'Supabase no configurado' } }
    const { error } = await supabase.auth.updateUser({ password: newPassword })
    return { error }
  }

  const value = {
    user,
    session,
    loading,
    profileReady,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updatePassword,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
